/**
 * Slimmy account opt-out page.
 *
 * Public, no auth required to land on it. The user authenticates with
 * username/email + password directly here; on success we call the
 * existing `POST /api/users/me/delete` endpoint with the freshly
 * obtained session token. The page lives at `/optout` (advertised on
 * Slimmy support pages / Google Play & App Store store listings so
 * users can self-service deletion without installing the app).
 *
 * Flow:
 *   1. Login form (identifier + password). On submit → /auth/login-email.
 *   2. Confirmation step: user must type "ELIMINA" (locale-aware) and
 *      tap the destructive button. On confirm → /users/me/delete.
 *   3. Success screen.
 */
import { useState } from 'react';
import { API_URL } from '../config/api';
import { AlertTriangle, Loader2, CheckCircle2, Trash2 } from 'lucide-react';

const API = `${API_URL}/api`;

export default function Optout() {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState(null);
  const [confirmationWord, setConfirmationWord] = useState('ELIMINA');
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onLogin = async (e) => {
    e?.preventDefault?.();
    setError('');
    if (!identifier.trim() || !password) {
      setError('Inserisci nome utente / email e password.');
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API}/auth/login-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: identifier.trim(), password }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.detail || 'Credenziali non valide.');
      }
      const data = await r.json();
      const tok = data?.session_token || data?.token || data?.access_token;
      if (!tok) throw new Error('Login fallito: nessun token ricevuto.');
      // Fetch the locale-correct confirmation word (Italian → ELIMINA,
      // English → DELETE, etc.) so we display & validate the right one.
      let word = 'ELIMINA';
      try {
        const cr = await fetch(`${API}/users/me/delete-confirmation`, {
          headers: { Authorization: `Bearer ${tok}` },
        });
        if (cr.ok) {
          const cd = await cr.json();
          if (cd?.confirmation_word) word = cd.confirmation_word;
        }
      } catch (_) { /* keep default */ }
      setConfirmationWord(word);
      setToken(tok);
    } catch (err) {
      setError(err?.message || 'Errore durante il login.');
    } finally {
      setLoading(false);
    }
  };

  const onConfirmDelete = async () => {
    setError('');
    if (confirmText.trim().toUpperCase() !== confirmationWord.toUpperCase()) {
      setError(`Scrivi ${confirmationWord} in maiuscolo per confermare.`);
      return;
    }
    setLoading(true);
    try {
      const r = await fetch(`${API}/users/me/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ confirmation: confirmText.trim() }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.detail || 'Eliminazione fallita.');
      }
      setDone(true);
    } catch (err) {
      setError(err?.message || 'Errore durante l\'eliminazione.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#080D1A] text-white flex flex-col items-center px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_57e2e740-274d-4480-9155-167f0e5dcf96/artifacts/q2sfm1zu_logo_white.png"
            alt="Slimmy"
            className="h-8 mx-auto mb-6"
          />
          <h1 className="font-display text-2xl mb-2">Elimina il tuo account</h1>
          <p className="text-white/60 text-sm">
            Conferma chi sei e procedi all'eliminazione. L'azione è
            <strong className="text-white"> irreversibile</strong>.
          </p>
        </div>

        {done ? (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h2 className="font-display text-xl mb-2">Account eliminato</h2>
            <p className="text-white/70 text-sm">
              Il tuo account Slimmy e tutti i dati associati sono stati rimossi.
            </p>
          </div>
        ) : !token ? (
          <form onSubmit={onLogin} className="space-y-3" data-testid="optout-login-form">
            <div>
              <label className="text-xs text-white/60 mb-1 block">Nome utente o email</label>
              <input
                type="text"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck="false"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none text-white placeholder:text-white/30"
                placeholder="es. mario o mario@email.com"
                data-testid="optout-identifier"
              />
            </div>
            <div>
              <label className="text-xs text-white/60 mb-1 block">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none text-white placeholder:text-white/30"
                placeholder="••••••••"
                data-testid="optout-password"
              />
            </div>
            {error && (
              <div className="flex items-start gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full mt-2 bg-[#E8334A] disabled:opacity-50 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
              data-testid="optout-login-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Continua
            </button>
          </form>
        ) : (
          <div className="space-y-3" data-testid="optout-confirm">
            <div className="flex items-start gap-2 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                Eliminando l'account perdi peso storico, foto, sfide, commenti e abbonamento.
                Nessun rimborso automatico per gli abbonamenti già pagati.
              </span>
            </div>
            <div>
              <label className="text-xs text-white/60 mb-1 block">
                Scrivi <strong className="text-white">{confirmationWord}</strong> per confermare
              </label>
              <input
                type="text"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck="false"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full px-3 py-3 rounded-xl bg-white/5 border border-white/10 outline-none text-white placeholder:text-white/30"
                placeholder={confirmationWord}
                data-testid="optout-confirm-text"
              />
            </div>
            {error && (
              <div className="flex items-start gap-2 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <button
              onClick={onConfirmDelete}
              disabled={loading}
              className="w-full mt-2 bg-red-600 disabled:opacity-50 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
              data-testid="optout-delete-submit"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Elimina definitivamente
            </button>
            <button
              onClick={() => { setToken(null); setConfirmText(''); setError(''); }}
              className="w-full text-white/60 text-sm py-2"
              data-testid="optout-cancel"
            >
              Annulla
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
